import { registerLocaleData } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import localeFr from '@angular/common/locales/fr';
import localeHu from '@angular/common/locales/hu';
import localePl from '@angular/common/locales/pl';
import localePt from '@angular/common/locales/pt';
import localeRo from '@angular/common/locales/ro';
import localeRu from '@angular/common/locales/ru';
import localeUk from '@angular/common/locales/uk';
import localeVi from '@angular/common/locales/vi';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import numbro from 'numbro';
import es from 'numbro/languages/es-ES';
import fr from 'numbro/languages/fr-FR';
//import hu from 'numbro/languages/hu-HU';
//import pl from 'numbro/languages/pl-PL';
//import pt from 'numbro/languages/pt-PT';
//import ro from 'numbro/languages/ro-RO';
//import ru from 'numbro/languages/ru-RU';
//import uk from 'numbro/languages/uk-UA';

import { ScopeService } from './scope.service';
import { AvailableLanguage, LanguageService } from './language.service';

@Injectable()
export class I18nService {

  public static AVAILABLE_LANGUAGES: AvailableLanguage[] = LanguageService.AVAILABLE_LANGUAGES;

  private currency: string = '€';

  constructor(
    private scope: ScopeService,
    private translate: TranslateService,
    private languageService: LanguageService,
  ) {
    registerLocaleData(localeEs);
    registerLocaleData(localeFr);
    registerLocaleData(localeHu);
    registerLocaleData(localePl);
    registerLocaleData(localePt);
    registerLocaleData(localeRo);
    registerLocaleData(localeRu);
    registerLocaleData(localeUk);
    registerLocaleData(localeVi);


    numbro.registerLanguage(es);
    numbro.registerLanguage(fr);
    //numbro.registerLanguage(hu);
    //numbro.registerLanguage(pl);
    //numbro.registerLanguage(pt);
    //numbro.registerLanguage(ro);
    //numbro.registerLanguage(ru);
    //numbro.registerLanguage(uk);


    this.scope.config$.subscribe((config) => this.setCurrency(config.currency));
  }

  public static formatCurrencyValue(value: number, currency: string, decPlaces?: number): string {
    let absValue = Math.abs(value);

    // Set default rounding under Million
    if (decPlaces === undefined || decPlaces === null) {
      decPlaces = value > 999999 ? 1 : 0;
    }

    // 2 decimal places => 100, 3 => 1000, etc
    decPlaces = Math.pow(10, decPlaces);

    // Enumerate number prefix
    const prefixes = ['k', 'M', 'B', 'T'];

    // Go through the array backwards, so we do the largest first
    for (let i = prefixes.length - 1; i >= 0; i--) {
      // Convert array index to "1000", "1000000", etc
      const size = Math.pow(10, (i + 1) * 3);

      // If the number is bigger or equal do the abbreviation
      if (size <= absValue) {
        // Here, we multiply by decPlaces, round, and then divide by decPlaces.
        // This gives us nice rounding to a particular decimal place.
        absValue = Math.round(absValue * decPlaces / size) / decPlaces;

        // Handle special case where we round up to the next abbreviation
        if ((absValue === 1000) && (i < prefixes.length - 1)) {
          absValue = 1;
          i++;
        }

        // Add the letter for the abbreviation
        return `${value < 0 ? '-' : ''}${absValue} ${prefixes[i]}${currency}`;
      }
    }

    return `${value < 0 ? '-' : ''}${Math.round(absValue * decPlaces) / decPlaces} ${currency}`;
  }

  private static SUPPORTED_LOCALE_PREFIXES = ['en', 'es', 'fr', 'hu', 'pl', 'pt', 'ro', 'ru', 'uk'];

  public getCulture(): string {
    const browserCulture = this.translate.getBrowserCultureLang();
    const baseLanguage = browserCulture?.split('-')[0]?.toLowerCase();
    if (baseLanguage && I18nService.SUPPORTED_LOCALE_PREFIXES.includes(baseLanguage)) {
      return browserCulture;
    }
    return 'en';
  }

  public setCurrency(currency: string): void {
    this.currency = currency;
  }

  public getCurrency(): string {
    return this.currency;
  }

  public formatCurrency(value: number, decPlaces?: number): string {
    return I18nService.formatCurrencyValue(value, this.getCurrency(), decPlaces);
  }

  public formatPrice(price: number, decPlaces?: number): string[] {
    if (!Number.isNaN(Number(price))) {
      return this.formatCurrency(price, decPlaces).split(' ');
    } else {
      return ['-', ''];
    }
  }

  public formatPriceWithThousands(value: number): string {
    return new Intl.NumberFormat(
      this.getCulture(), { style: 'decimal' }
    ).format(Math.round(value))
      + ' '
      + this.getCurrency()
  }

  public language$ = this.languageService.language$;

  public setLanguage(language?: AvailableLanguage): void {

    // The user has manually selected a language so we need to refresh the window
    if (language) {
      localStorage.setItem('language', language);
      setTimeout(() => {
        location.reload();
      }, 50);
    } else {
      const cachedFilter = <AvailableLanguage>localStorage.getItem('language');
      // Check if a language is cached
      if (cachedFilter && I18nService.AVAILABLE_LANGUAGES.includes(cachedFilter)) {
        this.languageService.setLanguage(cachedFilter);
      } else {
        // If no language is cached yet, we use the browser language if french, else we use english
        const browserLang = <AvailableLanguage>this.translate.getBrowserLang();
        if (I18nService.AVAILABLE_LANGUAGES.includes(browserLang)) {
          this.languageService.setLanguage(browserLang);
        } else {
          this.languageService.setLanguage('en');
        }
      }
    }
  };

  public formatNumber(value: number): string {
    return new Intl.NumberFormat(
      this.getCulture(), { style: 'decimal' }
    ).format(value);
  }
}
